import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Typography
} from "@material-ui/core";
import {
  Refresh as RefreshIcon,
  Storage as StorageIcon,
  Memory as MemoryIcon,
  Computer as ComputerIcon,
  WhatsApp as WhatsAppIcon,
  ListAlt as ListAltIcon
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { Redirect } from "react-router-dom";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import { getServerReport } from "../../services/serverReport";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  card: {
    borderRadius: 16,
    boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
    border: "1px solid rgba(148,163,184,0.22)"
  },
  cardTitle: {
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    gap: 8
  },
  metricValue: {
    fontWeight: 800,
    fontSize: 26,
    color: theme.palette.primary.main
  },
  muted: {
    color: theme.palette.text.secondary
  },
  contentScroll: {
    flex: 1,
    minHeight: 0,
    maxHeight: "calc(100vh - 150px)",
    overflowY: "auto",
    overflowX: "hidden",
    paddingRight: 4,
    paddingBottom: 64,
    [theme.breakpoints.down("sm")]: {
      maxHeight: "calc(100vh - 120px)",
      paddingBottom: 96
    }
  },
  logBox: {
    background: theme.palette.type === "dark" ? "#111827" : "#0f172a",
    color: "#e5e7eb",
    padding: 16,
    borderRadius: 12,
    fontFamily: "monospace",
    fontSize: 12,
    maxHeight: 360,
    overflow: "auto",
    whiteSpace: "pre-wrap"
  },
  statusOnline: {
    backgroundColor: "#dcfce7",
    color: "#166534",
    fontWeight: 700
  },
  statusOffline: {
    backgroundColor: "#fee2e2",
    color: "#991b1b",
    fontWeight: 700
  }
}));

const formatUptime = seconds => {
  if (!seconds) return "0 min";

  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const StatusChip = ({ online, label }) => {
  const classes = useStyles();

  return (
    <Chip
      size="small"
      label={`${label}: ${online ? "Online" : "Offline"}`}
      className={online ? classes.statusOnline : classes.statusOffline}
    />
  );
};

const MetricCard = ({ title, value, subtitle, icon }) => {
  const classes = useStyles();

  return (
    <Card className={classes.card}>
      <CardContent>
        <Typography className={classes.cardTitle} variant="subtitle1">
          {icon}
          {title}
        </Typography>

        <Box mt={2}>
          <Typography className={classes.metricValue}>
            {value}
          </Typography>
          <Typography variant="body2" className={classes.muted}>
            {subtitle}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const ServerReport = () => {
  const classes = useStyles();

  const { user } = useContext(AuthContext);
  const isSuperAdmin = user?.super === true || user?.super === "true";

  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const loadReport = async () => {
    try {
      setLoading(true);
      const data = await getServerReport();
      setReport(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      loadReport();
    }
  }, [isSuperAdmin]);

  const backendErrors = report?.logs?.backendErrors || [];
  const backendLogs = report?.logs?.backendLogs || [];
  const pm2 = report?.pm2 || [];
  const whatsapps = report?.whatsapps || [];

  if (user && !isSuperAdmin) {
    return <Redirect to="/tickets" />;
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>Relatório do Servidor</Title>

        <Button
          variant="contained"
          color="primary"
          startIcon={loading ? <CircularProgress size={18} /> : <RefreshIcon />}
          onClick={loadReport}
          disabled={loading}
        >
          Atualizar
        </Button>
      </MainHeader>

      <Box className={classes.contentScroll}>
        {loading && !report ? (
          <Box display="flex" justifyContent="center" mt={6}>
            <CircularProgress />
          </Box>
        ) : (
          <>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <MetricCard
                title="Servidor"
                icon={<ComputerIcon />}
                value={report?.server?.hostname || "-"}
                subtitle={`Tempo de atividade: ${formatUptime(report?.server?.uptimeSeconds)}`}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <MetricCard
                title="CPU"
                icon={<MemoryIcon />}
                value={`${report?.server?.cpus || 0} cores`}
                subtitle={`Carga: ${(report?.server?.loadAverage || []).map(v => Number(v).toFixed(2)).join(" / ")}`}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Card className={classes.card}>
                <CardContent>
                  <Typography className={classes.cardTitle} variant="subtitle1">
                    <MemoryIcon />
                    Memória RAM
                  </Typography>

                  <Box mt={2}>
                    <Typography className={classes.metricValue}>
                      {report?.memory?.usedPercent || 0}%
                    </Typography>
                    <Typography variant="body2" className={classes.muted}>
                      {report?.memory?.usedGB || 0} GB usado de {report?.memory?.totalGB || 0} GB
                    </Typography>
                    <Box mt={1}>
                      <LinearProgress variant="determinate" value={report?.memory?.usedPercent || 0} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card className={classes.card}>
                <CardContent>
                  <Typography className={classes.cardTitle} variant="subtitle1">
                    <StorageIcon />
                    Disco
                  </Typography>

                  <Box mt={2}>
                    <Typography className={classes.metricValue}>
                      {report?.disk?.usedPercent || 0}%
                    </Typography>
                    <Typography variant="body2" className={classes.muted}>
                      {report?.disk?.usedGB || 0} GB usado de {report?.disk?.totalGB || 0} GB
                    </Typography>
                    <Box mt={1}>
                      <LinearProgress variant="determinate" value={report?.disk?.usedPercent || 0} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box mt={3}>
            <Card className={classes.card}>
              <CardContent>
                <Typography className={classes.cardTitle} variant="h6">
                  <StorageIcon />
                  Serviços
                </Typography>

                <Box mt={2} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                  <StatusChip label="Postgres" online={report?.services?.postgres} />
                  <StatusChip label="Redis" online={report?.services?.redis} />
                  <StatusChip label="Backend" online={report?.services?.backendPort} />
                  <StatusChip label="Frontend" online={report?.services?.frontendPort} />
                </Box>

                <Box mt={2}>
                  <Typography variant="body2" className={classes.muted}>
                    Node: {report?.server?.nodeVersion} • Plataforma: {report?.server?.platform} {report?.server?.arch}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box mt={3}>
            <Card className={classes.card}>
              <CardContent>
                <Typography className={classes.cardTitle} variant="h6">
                  <ListAltIcon />
                  Processos PM2
                </Typography>

                <Box mt={2}>
                  {pm2.length === 0 ? (
                    <Typography variant="body2" className={classes.muted}>
                      Nenhum processo PM2 encontrado.
                    </Typography>
                  ) : (
                    pm2.map(app => (
                      <Paper key={app.name} variant="outlined" style={{ padding: 12, marginBottom: 8, borderRadius: 12 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                          <Box>
                            <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                              {app.name}
                            </Typography>
                            <Typography variant="body2" className={classes.muted}>
                              PID: {app.pid} • Restarts: {app.restarts} • RAM: {app.memoryMB} MB • CPU: {app.cpu}%
                            </Typography>
                          </Box>

                          <Chip
                            size="small"
                            label={app.status}
                            className={app.status === "online" ? classes.statusOnline : classes.statusOffline}
                          />
                        </Box>
                      </Paper>
                    ))
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box mt={3}>
            <Card className={classes.card}>
              <CardContent>
                <Typography className={classes.cardTitle} variant="h6">
                  <WhatsAppIcon />
                  Conexões WhatsApp
                </Typography>

                <Box mt={2}>
                  {whatsapps.length === 0 ? (
                    <Typography variant="body2" className={classes.muted}>
                      Nenhuma conexão encontrada.
                    </Typography>
                  ) : (
                    whatsapps.map(w => (
                      <Paper key={w.id} variant="outlined" style={{ padding: 12, marginBottom: 8, borderRadius: 12 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                          <Box>
                            <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                              #{w.id} — {w.name}
                            </Typography>
                            <Typography variant="body2" className={classes.muted}>
                              Empresa: {w.companyId} • Tipo: {w.type || "whatsapp"} • Atualizado: {w.updatedAt ? new Date(w.updatedAt).toLocaleString("pt-BR") : "-"}
                            </Typography>
                            {w.importOldMessages && (
                              <Typography variant="body2" className={classes.muted}>
                                Importação ativa: {new Date(w.importOldMessages).toLocaleString("pt-BR")} até {new Date(w.importRecentMessages).toLocaleString("pt-BR")}
                              </Typography>
                            )}
                          </Box>

                          <Chip
                            size="small"
                            label={w.status}
                            className={w.status === "CONNECTED" ? classes.statusOnline : classes.statusOffline}
                          />
                        </Box>
                      </Paper>
                    ))
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box mt={3}>
            <Card className={classes.card}>
              <CardContent>
                <Typography className={classes.cardTitle} variant="h6">
                  Últimos erros do backend
                </Typography>

                <Box mt={2} className={classes.logBox}>
                  {backendErrors.length ? backendErrors.join("\n") : "Nenhum erro recente encontrado."}
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box mt={3} mb={4}>
            <Card className={classes.card}>
              <CardContent>
                <Typography className={classes.cardTitle} variant="h6">
                  Últimos logs do backend
                </Typography>

                <Box mt={2} className={classes.logBox}>
                  {backendLogs.length ? backendLogs.join("\n") : "Nenhum log recente encontrado."}
                </Box>
              </CardContent>
            </Card>
          </Box>
          </>
        )}
      </Box>
    </MainContainer>
  );
};

export default ServerReport;